import { useState } from "react";
import {
  useFirestoreGeneralArrayContains,
  useFirestoreGeneralOrderBy,
} from "../../firebase/useFirestore";
import questionTypes from "../../utils/questionType";
import shouldShowMatrixRightColumn from "../../helpers/questionnaire/shouldShowMatrixRightColumn";
import TocEmptyChip from "./TocEmptyChip.jsx";
import scaleStartLabel from "../../helpers/questionnaire/scaleStartLabel";
import useSharedCollection from "../../firebase/useSharedCollection";
import useHasResponses from "../../hooks/useHasResponses";
import indicatorLink from "../../helpers/impactStrategy/indicatorLink";

/**
 * @typedef {object} IndicatorRow
 * @property {string} key - stable identity (doc ID)
 * @property {string} title - question text / output title
 * @property {'field' | 'output'} kind - which collection the row came from
 * @property {number} position - sort key within the list
 * @property {object} raw - the Firestore doc
 * @property {import('../../helpers/impactStrategy/indicatorLink').IndicatorLink | null} [link] - onderzoek/vragenlijst (questions only; null while loading)
 */

/**
 * Read-only indicator list for ONE effect in the TOC navigator's detail view.
 * A count alone told the user nothing, so the list replaces it: every indicator
 * is a row that expands to its actual content (uitleg, schaal, antwoordopties,
 * matrixrijen) — bekijken, niet bewerken. Beheren gebeurt in de editor.
 *
 * Same two sources as IndicatorsCount (vragen + gekoppelde outputs), queried
 * without an orderBy: that would silently drop docs missing the sort field
 * (docs/pitfalls.md#database-firestore-orderby-excludes-missing-field).
 *
 * @param {object} props
 * @param {string} props.effectId - OutputEffects.ID of the active effect node
 * @param {() => void} [props.onEdit] - opens the inline editor; makes the empty
 *   placeholder a shortcut into it
 */
const EffectIndicatorList = ({ effectId, onEdit }) => {
  // State
  const [openId, setOpenId] = useState(/** @type {string | null} */ (null));

  // Firestore
  const fields = useFirestoreGeneralArrayContains("QuestionnaireFields", "EffectId", effectId || "");
  const outputs = useFirestoreGeneralArrayContains("Outputs", "EffectId", effectId || "");
  // Which research each question hangs on (inbox #328). Shared listeners: the
  // tenant's Research is already open elsewhere; Questionnaires only when a
  // question sits in a questionnaire that no research uses (then its name is
  // all there is to show).
  const researches = useSharedCollection("Research");
  const needsQuestionnaires = Array.isArray(fields) && Array.isArray(researches)
    && fields.some((f) => f.QuestionnaireID && !researches.some((r) => r.QuestionnaireID === f.QuestionnaireID));
  const questionnaires = useSharedCollection(needsQuestionnaires ? "Questionnaires" : "");

  // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  // The read engine returns '' until the first snapshot lands. "Nog geen
  // indicatoren" is a claim to the reader, and it is untrue while the data is
  // still on its way — so 'weet ik nog niet' gets its own branch.
  const loaded = Array.isArray(fields) && Array.isArray(outputs);

  if (!loaded) return <p className="tocnav-block-text">Indicatoren laden…</p>;

  /** @type {IndicatorRow[]} */
  const rows = [
    ...fields.map((f) => ({
      key: f.ID,
      title: f.Question || "Naamloze indicator",
      kind: /** @type {'field'} */ ("field"),
      position: f.IndicatorPosition ?? f.Position ?? 0,
      raw: f,
      link: indicatorLink(f, researches, questionnaires),
    })),
    ...outputs.map((o) => ({
      key: o.ID,
      title: o.Title || "Naamloze output",
      kind: /** @type {'output'} */ ("output"),
      position: o.Position ?? 0,
      raw: o,
    })),
  ].sort((a, b) => a.position - b.position);

  if (rows.length === 0) {
    return <TocEmptyChip onClick={onEdit}>Nog geen indicatoren</TocEmptyChip>;
  }

  return (
    <div className="tocnav-indicators">
      <ul className="tocnav-indicator-list">
        {rows.map((row) => (
          <IndicatorRow
            key={row.key}
            row={row}
            open={openId === row.key}
            onToggle={() => setOpenId((current) => (current === row.key ? null : row.key))}
          />
        ))}
      </ul>
    </div>
  );
};

/**
 * One indicator in the list: a clickable row that folds open to its content.
 *
 * Matrix questions from the Meetstandaard all share the same vraagtekst ("In
 * welke mate ben je het eens met de volgende stellingen?"), so the collapsed
 * rows are indistinguishable without their first statement — hence the
 * subtitle. The matrixrijen are fetched here (not in the detail) so the row and
 * its open panel share one listener.
 *
 * @param {object} props
 * @param {IndicatorRow} props.row - the normalised row
 * @param {boolean} props.open - whether this row is folded open
 * @param {() => void} props.onToggle - folds this row open/closed
 */
const IndicatorRow = ({ row, open, onToggle }) => {
  const isField = row.kind === "field";
  const isMatrix = isField && (row.raw.Type || "").startsWith("matrix");
  // Is er al geantwoord op deze vraag? Ja/nee, geen telling — null = nog niet
  // bekend (of de read mislukte): dan staat er niets.
  // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  const hasData = useHasResponses("FieldID", isField ? row.raw.ID : "");

  // Firestore — '' for every non-matrix row, so the engine skips the listener.
  const matrixRows = useFirestoreGeneralOrderBy(
    "MatrixQuestionRows", "Field", isMatrix ? row.raw.ID : "", "Position", "asc");

  const statements = Array.isArray(matrixRows) ? matrixRows : [];
  const extra = statements.length - 1;
  const subtitle = statements.length === 0
    ? ""
    : [
        statements[0].Title || "Naamloze stelling",
        extra > 0 ? ` + ${extra} andere ${extra === 1 ? "stelling" : "stellingen"}` : "",
      ].join("");

  return (
    <li className="tocnav-indicator-item">
      <button type="button" className="tocnav-indicator-head" aria-expanded={open} onClick={onToggle}>
        <span className="tocnav-indicator-heading">
          <span className="tocnav-indicator-title">{row.title}</span>
          {subtitle && <span className="tocnav-indicator-subtitle">{subtitle}</span>}
        </span>
        {/* Rechts uitgelijnd: aan welk onderzoek de vraag hangt en of er al
            data op zit (inbox #328). Alleen voor vragen; een gekoppelde output
            heeft geen onderzoek. */}
        {isField && (
          <span className="tocnav-indicator-meta">
            {row.link && <span className={`tocnav-indicator-link is-${row.link.kind}`}>{row.link.label}</span>}
            {hasData !== null && (
              <span className={`tocnav-indicator-data${hasData ? " has-data" : ""}`}>
                {hasData ? "Data aanwezig" : "Nog geen data"}
              </span>
            )}
          </span>
        )}
        <svg viewBox="0 0 24 24" fill="none" className="tocnav-section-chev">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        row.kind === "field"
          ? <FieldIndicatorDetail field={row.raw} matrixRows={statements} link={row.link} />
          : <OutputIndicatorDetail output={row.raw} />
      )}
    </li>
  );
};

/**
 * Read-only content of one question indicator: uitleg, vraagsoort and the
 * answer shape that belongs to that soort.
 *
 * @param {object} props
 * @param {object} props.field - the QuestionnaireFields doc
 * @param {Array<object>} props.matrixRows - matrixrijen, already fetched by the row
 * @param {import('../../helpers/impactStrategy/indicatorLink').IndicatorLink | null} [props.link] - onderzoek/vragenlijst; the row shows a short label, this lists every name
 */
const FieldIndicatorDetail = ({ field, matrixRows, link }) => {
  const type = field.Type || "";
  const isMultiple = type.startsWith("multiple");
  const isMatrix = type.startsWith("matrix");

  // Firestore — always called (hooks rule); the types that do not need a query
  // pass '' so the engine short-circuits instead of subscribing.
  const options = useFirestoreGeneralOrderBy(
    "MultipleQuestionOptions", "Field", isMultiple ? field.ID : "", "Position", "asc");
  const matrixColumns = useFirestoreGeneralOrderBy(
    "MatrixQuestionColumns", "Field", isMatrix ? field.ID : "", "Position", "asc");

  // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
  // Een kop zonder chips eronder beweert 'er zijn er geen'. Zolang de read niet
  // geland is ('' uit de engine) blijft de hele groep weg.
  const answerOptions = Array.isArray(options) ? options : [];
  const answerColumns = Array.isArray(matrixColumns) ? matrixColumns : [];

  const bipolar = shouldShowMatrixRightColumn(field);
  // AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-name-mismatch
  // Twee schrijvers, twee spellingen (ReachStartLable / ReachStartLabel):
  // lees via scaleStartLabel, anders valt het label stil weg (inbox #313).
  const scaleBound = (value, label) =>
    [value, label].filter((part) => part !== "" && part !== undefined && part !== null).join(" — ");

  return (
    <div className="tocnav-indicator-detail">
      {field.Explainer && <p className="tocnav-block-text">{field.Explainer}</p>}
      <div className="tocnav-chips">
        <span className="tocnav-chip">{questionTypes(type)}</span>
        {field.Standard === "true" && <span className="tocnav-chip">Meetstandaard</span>}
      </div>

      {link && link.kind !== "none" && (
        <div className="tocnav-indicator-answers">
          <div className="tocnav-taxo-group-label">
            {link.kind === "research"
              ? (link.names.length === 1 ? "Onderzoek" : "Onderzoeken")
              : "Vragenlijst (nog in geen onderzoek)"}
          </div>
          <div className="tocnav-chips">
            {link.names.map((name) => <span className="tocnav-chip" key={name}>{name}</span>)}
          </div>
        </div>
      )}

      {type === "scale" && (
        <div className="tocnav-indicator-answers">
          <div className="tocnav-taxo-group-label">Schaal</div>
          <div className="tocnav-chips">
            <span className="tocnav-chip">
              {scaleBound(field.ReachStart, scaleStartLabel(field))}
            </span>
            <span className="tocnav-chip">
              {scaleBound(field.ReachEnd, field.ReachEndLabel || field.ReachEndLable)}
            </span>
          </div>
        </div>
      )}

      {isMultiple && answerOptions.length > 0 && (
        <div className="tocnav-indicator-answers">
          <div className="tocnav-taxo-group-label">Antwoordopties</div>
          <div className="tocnav-chips">
            {answerOptions.map((option) => (
              <span className="tocnav-chip" key={option.ID}>{option.Option || "Naamloze optie"}</span>
            ))}
          </div>
        </div>
      )}

      {isMatrix && matrixRows.length > 0 && (
        <div className="tocnav-indicator-answers">
          <div className="tocnav-taxo-group-label">Rijen</div>
          <div className="tocnav-chips">
            {matrixRows.map((matrixRow) => (
              <span className="tocnav-chip" key={matrixRow.ID}>
                {bipolar && matrixRow.TitleRight
                  ? `${matrixRow.Title || "Naamloze rij"} ↔ ${matrixRow.TitleRight}`
                  : (matrixRow.Title || "Naamloze rij")}
              </span>
            ))}
          </div>
        </div>
      )}

      {isMatrix && answerColumns.length > 0 && (
        <div className="tocnav-indicator-answers">
          <div className="tocnav-taxo-group-label">Kolommen</div>
          <div className="tocnav-chips">
            {answerColumns.map((column) => (
              <span className="tocnav-chip" key={column.ID}>{column.Title || "Naamloze kolom"}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Read-only content of an output that is linked to the effect as an indicator.
 *
 * @param {object} props
 * @param {object} props.output - the Outputs doc
 */
const OutputIndicatorDetail = ({ output }) => (
  <div className="tocnav-indicator-detail">
    {output.Description && <p className="tocnav-block-text">{output.Description}</p>}
    <div className="tocnav-chips">
      <span className="tocnav-chip">Output</span>
    </div>
  </div>
);

export default EffectIndicatorList;
